/**
 * Session Storage utility library for managing client-side session data
 */
import logger from '@/lib/utils/logger'

// Session storage keys
export const SESSION_STORAGE_KEYS = {
  FOR_CUSTOMER_ID: 'forCustomerId',
} as const

/**
 * Set a value in session storage
 * @param key - The key to store the value under
 * @param value - The value to store (will be JSON stringified)
 */
export const setSessionStorageItem = <T>(key: string, value: T): void => {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      sessionStorage.setItem(key, JSON.stringify(value))
    }
  } catch (error) {
    logger.error('Error setting session storage item:', error)
  }
}

/**
 * Get a value from session storage
 * @param key - The key to retrieve the value for
 * @returns The parsed value or null if not found/error
 */
export const getSessionStorageItem = <T>(key: string): T | null => {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      const item = sessionStorage.getItem(key)
      return item ? JSON.parse(item) : null
    }
    return null
  } catch (error) {
    logger.error('Error getting session storage item:', error)
    return null
  }
}

/**
 * Remove a value from session storage
 * @param key - The key to remove
 */
export const removeSessionStorageItem = (key: string): void => {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      sessionStorage.removeItem(key)
    }
  } catch (error) {
    logger.error('Error removing session storage item:', error)
  }
}

/**
 * Clear all session storage items
 */
export const clearSessionStorage = (): void => {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      sessionStorage.clear()
    }
  } catch (error) {
    logger.error('Error clearing session storage:', error)
  }
}

/**
 * Check if session storage is available
 * @returns true if session storage is available, false otherwise
 */
export const isSessionStorageAvailable = (): boolean => {
  try {
    return typeof window !== 'undefined' && !!window.sessionStorage
  } catch (error) {
    return false
  }
}

// Convenience functions for specific session storage items

/**
 * Set the forCustomerId in session storage
 * @param forCustomerId - The customer ID to store
 */
export const setForCustomerId = (forCustomerId: string): void => {
  setSessionStorageItem(SESSION_STORAGE_KEYS.FOR_CUSTOMER_ID, forCustomerId)
}

/**
 * Get the forCustomerId from session storage
 * @returns The customer ID or null if not found
 */
export const getForCustomerId = (): string | null => {
  return getSessionStorageItem<string>(SESSION_STORAGE_KEYS.FOR_CUSTOMER_ID)
}

/**
 * Remove the forCustomerId from session storage
 */
export const removeForCustomerId = (): void => {
  removeSessionStorageItem(SESSION_STORAGE_KEYS.FOR_CUSTOMER_ID)
}
